/**
 * Die Rechnerei hinter der befristeten Pause („nicht verfügbar bis …“).
 *
 * Ein Wert, zwei Daten: `unavailableUntil` ist der **letzte Tag der
 * Abwesenheit**, wieder aktiv ist das Profil am Tag darauf. Beide werden
 * überall nebeneinander angezeigt — „bis 14.08.“ allein lässt offen, ob der
 * 14. noch dazugehört, und genau daran scheitert sonst jede Absprache.
 *
 * Geschickt wird an die Schnittstelle immer ein reiner Tag (`2026-08-14`), ohne
 * Uhrzeit und ohne Zeitzone; das Backend normiert ihn auf das Ende dieses Tages
 * (`services/supporter/availability`). Angezeigt wird über `formatDay`, das
 * bewusst die **UTC**-Bestandteile liest: Der zurückgelieferte Wert steht auf
 * 23:59:59.999 UTC, und in Wien (UTC+2) läge er örtlich schon am Folgetag — das
 * Profil wäre um einen Tag verschoben.
 *
 * Ohne Modul-Importe, damit sich die Rechnerei allein prüfen lässt
 * (`unavailability.test.js`).
 */

/** Die drei Einheiten des Fensters, in der Reihenfolge der Auswahl. */
export const DURATION_UNITS = ['days', 'weeks', 'months']

const pad = number => String(number).padStart(2, '0')

/** Ein Datum als reiner Tag, aus den örtlichen Bestandteilen: `2026-08-14`. */
export const toIsoDay = date =>
	`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/**
 * Monate addieren, ohne in den übernächsten zu rutschen.
 *
 * `new Date(2026, 0, 31).setMonth(1)` ergibt den 3. März: Den 31. Februar gibt
 * es nicht, und JavaScript rechnet stillschweigend weiter. Wer am 31. Jänner
 * „1 Monat“ wählt, meint aber Ende Februar.
 */
const addMonths = (date, count) => {
	const target = new Date(date.getFullYear(), date.getMonth() + count, 1)
	const lastOfMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
	target.setDate(Math.min(date.getDate(), lastOfMonth))
	return target
}

/**
 * Der Tag, an dem das Profil wieder aktiv ist: Startpunkt plus gewählte Dauer.
 * „2 Wochen“ ab heute heißt also, dass heute in zwei Wochen wieder gearbeitet
 * wird — die Pause dauert genau 14 Tage.
 */
export const activeAgainFrom = (count, unit, from = new Date()) => {
	const amount = Math.floor(Number(count))
	if (!Number.isFinite(amount) || amount < 1) return null

	const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())

	if (unit === 'months') return addMonths(start, amount)

	const days = unit === 'weeks' ? amount * 7 : amount
	start.setDate(start.getDate() + days)
	return start
}

/** Der letzte Tag der Abwesenheit als reiner Tag — der Tag vor der Rückkehr. */
export const lastDayFrom = (count, unit, from = new Date()) => {
	const back = activeAgainFrom(count, unit, from)
	if (!back) return null

	back.setDate(back.getDate() - 1)
	return toIsoDay(back)
}

/** Datum in österreichischer Schreibweise (14.08.2026). Liest UTC — siehe oben. */
export const formatDay = value => {
	if (!value) return null

	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return null

	return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`
}

/** „wieder aktiv am 15.08.2026“ — der Tag nach dem letzten Abwesenheitstag. */
export const activeAgainLabel = until => {
	if (!until) return null

	const date = new Date(until)
	if (Number.isNaN(date.getTime())) return null

	return formatDay(
		new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1))
	)
}

/** Frühester wählbarer letzter Abwesenheitstag: heute. */
export const earliestLastDay = (from = new Date()) => toIsoDay(from)
